import { useGetQBankStructureLevelItemsAbilityByQBankStructureIdQuery } from '@exam-management/graphql';
import { type QBankStructureLevel } from '@exam-management/qbs-provider';
import { useParams } from '@exam-management/router';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useQBankStructureLevelFormContext } from '../qbank-structure-level-form-provider';

import type {
  AddQBankStructureLevelItemsAbility,
  EditQBankStructureLevelItemContextType,
  EditQBankStructureLevelItemProviderProps,
  QBSLevelItemExcludingTag,
} from './types';
import {
  changeQBankStructureLevelItemToInfoItem,
  changeQBankStructureLevelItemToNonInfoItem,
  createQBankStructureLevelItem,
  deleteQBankStructureLevelItemsById,
  duplicateQBankStructureLevelItems,
  updateOrderOfLevelItemOnNameChange,
} from './utils';

export const EditQBankStructureLevelItemContext = createContext<
  EditQBankStructureLevelItemContextType | undefined
>(undefined);

export function EditQBankStructureLevelItemProvider(
  props: Readonly<EditQBankStructureLevelItemProviderProps>,
) {
  const { children, initialQBSLevelItems } = props;
  const [levelItems, setLevelItems] = useState<QBSLevelItemExcludingTag[]>(initialQBSLevelItems);
  const { setValue, getValues, register } = useQBankStructureLevelFormContext();

  const { structureId } = useParams('/qbank-structures/:structureId');

  const { data: getQBankLevelItemsAbility } =
    useGetQBankStructureLevelItemsAbilityByQBankStructureIdQuery({
      variables: {
        qBankStructureId: structureId,
      },
    });

  const qBankStructureLevelItemsAbility =
    getQBankLevelItemsAbility?.qbsGetQBankStructureLevelItemsAbilityByQBankStructureId;

  const addQBankStructureLevelItemsAbility: AddQBankStructureLevelItemsAbility = useMemo(() => {
    const map = new Map();
    qBankStructureLevelItemsAbility?.forEach((item) => {
      map.set(item.id, item.isChildrenAddable);
    });
    return map;
  }, [qBankStructureLevelItemsAbility]);

  const updateNameOfQBankStructureLevelItem = useCallback(
    (id: string, name: string | null, prevName: string | null) => {
      const result = updateOrderOfLevelItemOnNameChange(levelItems, id, name, prevName);
      setLevelItems(result);
    },
    [levelItems],
  );

  const addChildOfQBankStructureLevelItem = useCallback(
    (id: string, levels: QBankStructureLevel[], qBankStructureId: string) => {
      const createdQBankStructureLevelItem = createQBankStructureLevelItem(
        id,
        levelItems,
        levels,
        qBankStructureId,
      );
      if (!createdQBankStructureLevelItem) return;

      addQBankStructureLevelItemsAbility.set(createdQBankStructureLevelItem.id, true);

      register(createdQBankStructureLevelItem.id, {
        value: undefined,
      });
      setLevelItems((prev) => [...prev, createdQBankStructureLevelItem]);
    },
    [levelItems, addQBankStructureLevelItemsAbility],
  );

  const duplicateQBankStructureLevelItem = useCallback(
    (id: string) => {
      const { duplicatedLevelItems, duplicatedIds } = duplicateQBankStructureLevelItems(
        id,
        levelItems,
      );
      if (!duplicatedLevelItems?.length) {
        return;
      }

      for (const [oldId, newId] of duplicatedIds.entries()) {
        const name = getValues(oldId);
        if (newId) {
          addQBankStructureLevelItemsAbility.set(newId, true);
          setValue(newId, name);
          register(newId, {
            value: name,
          });
        } else {
          addQBankStructureLevelItemsAbility.set(oldId, true);
        }
      }

      setLevelItems((prev) => {
        return [...prev, ...duplicatedLevelItems];
      });
    },
    [levelItems, addQBankStructureLevelItemsAbility],
  );

  const deleteQBankStructureLevelItem = useCallback(
    (id: string) => {
      setLevelItems((prevLevelItems) => deleteQBankStructureLevelItemsById(id, prevLevelItems));
    },
    [levelItems, addQBankStructureLevelItemsAbility],
  );

  const updateQBankStructureLevelItemToNonInfoItem = useCallback(
    (id: string, levels: QBankStructureLevel[]) => {
      setLevelItems(
        (prevLevelItems) =>
          changeQBankStructureLevelItemToNonInfoItem(id, prevLevelItems, levels) ?? prevLevelItems,
      );
    },
    [levelItems],
  );

  const updateQBankStructureLevelItemToInfoItem = useCallback(
    (id: string) => {
      setLevelItems((prevLevelItems) =>
        changeQBankStructureLevelItemToInfoItem(id, prevLevelItems),
      );
    },
    [levelItems],
  );

  const resetQBStructureLevelItems = useCallback(() => {
    setLevelItems(initialQBSLevelItems);
  }, [initialQBSLevelItems, levelItems]);

  const value = useMemo(
    () => ({
      levelItems,
      updateNameOfQBankStructureLevelItem,
      addChildOfQBankStructureLevelItem,
      duplicateQBankStructureLevelItem,
      deleteQBankStructureLevelItem,
      updateQBankStructureLevelItemToNonInfoItem,
      updateQBankStructureLevelItemToInfoItem,
      resetQBStructureLevelItems,
      addQBankStructureLevelItemsAbility,
    }),
    [
      levelItems,
      updateNameOfQBankStructureLevelItem,
      addChildOfQBankStructureLevelItem,
      duplicateQBankStructureLevelItem,
      deleteQBankStructureLevelItem,
      updateQBankStructureLevelItemToNonInfoItem,
      updateQBankStructureLevelItemToInfoItem,
      resetQBStructureLevelItems,
      addQBankStructureLevelItemsAbility,
    ],
  );

  useEffect(() => {
    setLevelItems(initialQBSLevelItems);
  }, [initialQBSLevelItems]);

  return (
    <EditQBankStructureLevelItemContext.Provider value={value}>
      {children}
    </EditQBankStructureLevelItemContext.Provider>
  );
}
